import React, { useMemo, useState } from "react";
import { generatePath, Link } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  message,
  Popconfirm,
  Space,
  TablePaginationConfig,
  TableProps,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { NumberParam, useQueryParams } from "use-query-params";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { ROUTE_PATHS } from "const/paths";
import { pageToSkip } from "utils/tableUtils";
import {
  useOrganizationDeleteMutation,
  useOrganizationListQuery,
} from "@ticketingplatform/api/dist/organization/hooks";
import { Organization } from "@ticketingplatform/api/dist/organization/__generated__/fragments";
import { GET_ORGANIZATIONS } from "@ticketingplatform/api/dist/organization";
import { DeleteIcon } from "assets/icons";
import { Permission } from "@ticketingplatform/api/dist/__generated__/globalTypes";
import useHasPermissions from "hooks/useHasPermissions";

import {
  UseOrganizationColumnReturnType,
  UseOrganizationListReturnType,
} from "./types";
import { PAGE_SIZE } from "./consts";

export const useOrganizationDelete = () => {
  const intl = useIntl();
  const [deleteOrganization, { data, loading }] = useOrganizationDeleteMutation(
    {
      evictQueries: [GET_ORGANIZATIONS],
    }
  );

  const handleConfirm = (id: number) =>
    deleteOrganization({
      variables: { id },
      onCompleted: () =>
        message.success(
          intl.formatMessage({
            defaultMessage: "Organization has been removed!",
          })
        ),
      onError: () =>
        message.error(
          intl.formatMessage({
            defaultMessage: "Cannot remove the organization",
          })
        ),
    });

  return {
    handleConfirm,
    loading,
    data,
  };
};

export const useOrganizationColumns = (): UseOrganizationColumnReturnType => {
  const intl = useIntl();
  const { handleConfirm } = useOrganizationDelete();
  const canDeleteOrganization = useHasPermissions([
    Permission.ORGANIZATION_DELETE,
  ]);

  const columns: ColumnsType<Organization> = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Name",
        }),
        dataIndex: "name",
        key: "name",

        render: (_, record) => {
          const id = record.id.toString();
          const organizationDetailsRoute = generatePath(
            ROUTE_PATHS.OrganizationDetails,
            { id }
          );

          return <Link to={organizationDetailsRoute}>{record.name}</Link>;
        },
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Actions",
        }),
        key: "action",
        render: (_, record) => {
          const id = record.id.toString();
          const organizationEditRoute = generatePath(
            ROUTE_PATHS.OrganizationEdit,
            { id }
          );
          const organizationDetailsRoute = generatePath(
            ROUTE_PATHS.OrganizationDetails,
            { id }
          );

          return (
            <Space size="middle">
              <Link to={organizationEditRoute}>
                <EditOutlined />
              </Link>
              <Link to={organizationDetailsRoute}>
                <EyeOutlined />
              </Link>
              {canDeleteOrganization && (
                <Popconfirm
                  title={intl.formatMessage({
                    defaultMessage:
                      "Are you sure you want to delete organization?",
                  })}
                  onConfirm={() => handleConfirm(record.id)}
                >
                  <DeleteIcon />
                </Popconfirm>
              )}
            </Space>
          );
        },
      },
    ],
    [canDeleteOrganization, handleConfirm, intl]
  );

  return { columns };
};

export const useOrganizationList = (): UseOrganizationListReturnType => {
  const [filters, setFilters] = useQueryParams({
    current: NumberParam,
    pageSize: NumberParam,
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
  });

  const { loading, data } = useOrganizationListQuery({
    variables: {
      take: filters.pageSize,
      skip: pageToSkip(filters.current || 1, PAGE_SIZE),
    },

    onCompleted: (_data) => {
      setPagination({
        ...pagination,
        total: _data?.organizations?.count,
      });
      setFilters({
        current: filters.current || 1,
        pageSize: pagination.pageSize || PAGE_SIZE,
      });
    },
  });
  const organizations = data?.organizations?.nodes || [];
  const tableChangeHandler: TableProps<Organization>["onChange"] = (
    _pagination
  ) => {
    setPagination(_pagination);
    setFilters(_pagination);
  };

  return {
    loading,
    pagination: {
      ...pagination,
      current: filters.current || 1,
      pageSize: filters.pageSize || PAGE_SIZE,
    },
    organizations,
    tableChangeHandler,
  };
};
